import React, { useState } from "react";
import UploadSong from "./mode/UploadSong";
import { FaCheck } from "react-icons/fa";
import { Check } from "lucide-react";
import ChooseVoices from "./ChooseVoices";
import ModelConvertSettings from "../conversion/ModelConvertSettings";
import { IoMdArrowBack } from "react-icons/io";
import styles from "../../styles/Conversion.module.css";
import { useFeatureFlagEnabled } from "posthog-js/react";

const CreateConversion = ({
  model,
  ip,
  setIsOpen,
  setUpgradeModalOpen,
  downloadReliability,
}) => {
  const [stage, setStage] = useState(0);
  const [finalUrl, setFinalUrl] = useState(null);
  const [title, setTitle] = useState("");
  const [librarySong, setLibrarySong] = useState(null);
  const [progress, setProgress] = useState(0);
  const [voices, setVoices] = useState([{ ...model, pitchScaling: 0 }]);
  const [mode, setMode] = useState("upload");
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [audioEffects, setAudioEffects] = useState([]);
  const stages = [
    {
      id: 0,
      name: "Audio",
    },
    {
      id: 1,
      name: "Add Voices",
    },
    {
      id: 2,
      name: "Settings",
    },
  ];

  const nextStage = () => {
    setStage(stage + 1);
  };
  const previousStage = () => {
    if (stage === 1) {
      setFinalUrl(null);
      setLibrarySong(null);
      setUploadedFileId(null);
      setProgress(0);
      setTitle(null);
    }
    setStage(stage - 1);
  };

  const addVoice = (voice) => {
    if (voices.some((v) => v._id === voice._id)) {
      // Remove the voice if it already exists in the array
      if (voice._id === model._id) return;
      setVoices(voices.filter((v) => v._id !== voice._id));
    } else {
      if (voices.length === 6) {
        return;
      }
      // Add the voice if it's not in the array
      setVoices([...voices, { ...voice, pitchScaling: 0 }]);
    }
  };

  const selectCombo = (combo, isComboSelected) => {
    if (isComboSelected) {
      setVoices([{ ...model, pitchScaling: 0 }]);
    } else {
      // Ensure every voice in the combo has pitchScaling set to 0
      const updatedVoices = combo.models.map((voice) => ({
        ...voice,
        pitchScaling: 0,
      }));
      setVoices(updatedVoices);
    }
  };

  const setPitchScalingForVoice = (voiceId, pitch) => {
    const updatedVoices = voices.map((voice) => {
      if (voice._id === voiceId) {
        return { ...voice, pitchScaling: pitch };
      }
      return voice;
    });
    setVoices(updatedVoices);
  };

  const [selectedButton, setSelectedButton] = useState("upload");

  return (
    <div className={`tw-w-full md:tw-min-w-[600px] md:tw-max-w-lg`}>
      {stage !== 0 && (
        <div
          onClick={() => {
            previousStage();
          }}
          className="tw-absolute tw-top-2.5 tw-left-2.5 tw-text-black tw-cursor-pointer tw-bg-gray-100 tw-p-2 tw-rounded-full tw-flex tw-items-center tw-flex tw-items-center tw-text-xs tw-font-black tw-gap-0.5 tw-tracking-wider"
        >
          <IoMdArrowBack size={16} strokeWidth={10} />
        </div>
      )}
      <div className="tw-flex tw-mb-0 tw-w-full tw-items-center tw-justify-between tw-p-1 tw-pb-2 tw-px-0 tw-rounded-3xl">
        {stages.map((item) => {
          return (
            <div
              key={item.id}
              className="tw-font-bold tw-relative tw-flex tw-justify-center tw-flex-col tw-items-center tw-gap-2 tw-text-black/30 tw-w-full"
            >
              <div className="tw-flex tw-h-3 tw-items-center">
                {stage === item.id ? (
                  <div className="tw-size-2 tw-bg-purple-700 tw-rounded-full" />
                ) : stage > item.id ? (
                  <Check
                    size={16}
                    strokeWidth={6}
                    className="tw-text-purple-700"
                  />
                ) : (
                  <div className="tw-size-2 tw-bg-black/20 tw-rounded-full" />
                )}
              </div>
              <div className="tw-text-sm tw-relative tw-w-full tw-items-center tw-flex tw-justify-center tw-text-center">
                {item.name}
              </div>
              {item.id !== 2 && (
                <div className="tw-absolute tw-left-1/2 tw-w-full tw-flex tw-items-center tw-justify-center">
                  <div
                    className={` tw-h-1 tw-w-1/3 md:tw-w-1/2 tw-rounded-full tw-transition-all   ${stage > item.id ? "tw-bg-purple-700" : "tw-bg-black/10"}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* <div className="tw-text-xl tw-font-black tw-text-center tw-mb-4">
        Create Conversion
      </div> */}
      {stage === 0 ? (
        <UploadSong
          model={model}
          nextStage={nextStage}
          setFinalUrl={setFinalUrl}
          setTitle={setTitle}
          setLibrarySong={setLibrarySong}
          setProgress={setProgress}
          setUpgradeModalOpen={setUpgradeModalOpen}
          setIsOpen={setIsOpen}
          mode={mode}
          setMode={setMode}
          uploadedFileId={uploadedFileId}
          setUploadedFileId={setUploadedFileId}
          selectedButton={selectedButton}
          setSelectedButton={setSelectedButton}
          setMetadata={setMetadata}
          downloadReliability={downloadReliability}
          setAudioEffects={setAudioEffects}
        />
      ) : stage === 1 ? (
        <ChooseVoices
          voices={voices}
          setVoices={setVoices}
          addVoice={addVoice}
          nextStage={nextStage}
          selectCombo={selectCombo}
          originalModel={model}
          previousStage={previousStage}
        />
      ) : (
        stage === 2 && (
          <ModelConvertSettings
            voices={voices}
            finalUrl={finalUrl}
            progress={progress}
            title={title}
            librarySong={librarySong}
            ip={ip}
            mode={librarySong ? "library" : mode}
            setIsOpen={setIsOpen}
            setUpgradeModalOpen={setUpgradeModalOpen}
            setPitchScalingForVoice={setPitchScalingForVoice}
            uploadedFileId={uploadedFileId}
            metadata={metadata}
            audioEffects={audioEffects}
          />
        )
      )}
    </div>
  );
};

export default CreateConversion;
